import {annualizedDsSalary, dsSalaryCoverage} from "@/utils/clubSnapshot";
import {
	summarizeClubWellness,
	summarizeDsMedicalDays,
} from "@/utils/clubSnapshot";
import {wellnessCoverage} from "@/utils/clubSnapshot";
import {
	summarizeAttendanceTeams,
	attendanceCoverage,
} from "@/utils/clubSnapshot";
import {useEffect, useState} from "react";
import moment from "moment";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsActivity,
	DsChips,
	DsEmpty,
	DsLink,
	DsMissingSources,
	DsResultWarnings,
	DsRing,
	DsSection,
	DsSkeleton,
	DsTeamSelect,
	DsTimelineEvent,
	DEPARTMENT_ICONS,
	dayLabel,
} from "@/components/DsKit";

import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BadgeIcon from "@mui/icons-material/Badge";
import EuroIcon from "@mui/icons-material/Euro";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import StadiumIcon from "@mui/icons-material/Stadium";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

import {useClubCertificates, useClubSnapshot} from "@/utils/hooks";
import {PIPELINE_STAGES, buildNeeds, formatEuro} from "@/utils/clubSnapshot";
import {
	briefFromSnapshot,
	generateDecisionBriefPDF,
} from "@/utils/generateDecisionBriefPDF";
import {ERROR_OPERATION} from "@/utils/constants";
import {
	buildActivities,
	buildChanges,
	buildTimeline,
	departmentLabel,
	groupByDay,
	loadPreviousVisit,
	loadSeenActivities,
	saveCurrentVisit,
	saveSeenActivities,
} from "@/utils/clubControl";

const STATUS_LABEL = {
	ok: "Sotto controllo",
	warn: "Da seguire",
	alert: "Intervenire",
	none: "Nessun dato",
	preview: "In arrivo",
};

const AREA_ICONS = {
	Squadre: GroupsIcon,
	Gare: SportsSoccerIcon,
	"Team manager": AssignmentTurnedInIcon,
	Performance: MonitorHeartIcon,
	"Area medica": LocalHospitalIcon,
	"Mercato & contratti": SwapHorizIcon,
	Scouting: TravelExploreIcon,
	Staff: BadgeIcon,
	Segreteria: AssignmentIcon,
	Economia: EuroIcon,
	"Strutture & logistica": StadiumIcon,
	Commerciale: HandshakeIcon,
};

const STATUS_SCORE = {ok: 100, warn: 60, alert: 25};

const pct = (v) => (v == null ? "—" : Math.round(v * 100) + "%");

// Indice di salute: media degli stati delle aree con dati reali
const healthOf = (areas) => {
	const scored = areas.filter(
		(a) => !a.preview && STATUS_SCORE[a.status] !== undefined,
	);
	if (!scored.length)
		return {value: null, tone: "green", text: "Dati insufficienti"};
	const value =
		scored.reduce((n, a) => n + STATUS_SCORE[a.status], 0) / scored.length;
	return value >= 80
		? {value, tone: "green", text: "Club in salute"}
		: value >= 60
			? {value, tone: "yellow", text: "Qualche area da seguire"}
			: {value, tone: "red", text: "Diverse aree richiedono intervento"};
};

const urgentLabel = (n) => (n === 1 ? "1 urgente" : `${n} urgenti`);

const greeting = () => {
	const hour = moment().hour();
	return hour < 13 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";
};

// Una card per ogni area del club: stato, numero chiave, due righe di contesto
const buildAreas = (s, certificates) => {
	const now = moment();
	const in7 = now.clone().add(7, "days");
	const in14 = now.clone().add(14, "days");
	const teams = s.teams;
	const players = s.totalPlayers;

	const nextWeekMatches = new Set(
		teams.flatMap((team) =>
			team.upcoming
				.filter((match) => moment(match.startDate).isBefore(in7))
				.map((match) => match.id),
		),
	).size;

	const lastWeek = teams
		.flatMap((t) => t.played)
		.filter((m) =>
			moment(m.startDate).isAfter(now.clone().subtract(7, "days")),
		);
	const lastWeekRecord = ["V", "N", "P"].map(
		(r) => lastWeek.filter((m) => m.result === r).length,
	);
	const season = teams.reduce(
		(acc, t) => {
			acc.V += t.record.V;
			acc.N += t.record.N;
			acc.P += t.record.P;
			return acc;
		},
		{V: 0, N: 0, P: 0},
	);

	const selectedWellness = summarizeClubWellness(teams);
	const medicalDays = summarizeDsMedicalDays(s);
	const avgTQR = selectedWellness?.avgTQR ?? null;
	const tqrCoverage = selectedWellness?.expected
		? selectedWellness.tqr.players / selectedWellness.expected
		: null;
	const lowTQR = teams.reduce((n, t) => n + t.wellness.lowTQR.length, 0);

	const injuredRatio = players ? s.activeInjuries.length / players : 0;
	const returning = (s.activeInjuryEpisodes || []).filter(
		(i) =>
			i.daysToDischarge !== null &&
			i.daysToDischarge > 0 &&
			i.daysToDischarge <= 7,
	).length;

	const exp90 = s.contracts.filter(
		(c) => c.daysLeft !== null && c.daysLeft <= 90,
	);
	const exp180 = s.contracts.filter(
		(c) => c.daysLeft !== null && c.daysLeft <= 180,
	);
	const gaps = buildNeeds(s).length;

	const pipelineCount = (id) =>
		s.pipeline.filter((p) => p.status === id).length;
	const observed14 = s.observedMatches.filter((m) =>
		moment(m.startDate).isBefore(in14),
	).length;

	const teamsNoStaff = teams.filter((t) => !t.staff.length).length;

	const attendanceSummary = summarizeAttendanceTeams(teams);
	const attendanceRate = attendanceSummary.rate;

	const toOrganize = teams.reduce(
		(n, t) =>
			n +
			t.organization.nextWeek.filter(
				(m) =>
					(m.convocations === 0 && m.daysTo <= 3) ||
					m.timeToConfirm ||
					m.fieldMissing,
			).length,
		0,
	);
	const sheets = teams.reduce(
		(n, t) => n + t.organization.incompleteSheets.length,
		0,
	);

	const fees = teams.reduce(
		(acc, t) => {
			acc.expected += t.fees.expected;
			acc.paid += t.fees.paid;
			acc.overdue += t.fees.overdue;
			acc.count += t.fees.count;
			return acc;
		},
		{expected: 0, paid: 0, overdue: 0, count: 0},
	);
	const certIssues = certificates?.tracked
		? [...certificates.byPlayer.values()].filter((c) =>
				["expired", "missing"].includes(c.status),
			).length
		: null;

	const annualSalary = annualizedDsSalary(s.monthlySalary);
	const budgetUse =
		s.finance.budget && annualSalary != null
			? annualSalary / s.finance.budget
			: null;

	return [
		{
			name: "Squadre",
			path: "/ds/squadre",
			status: teams.length ? "ok" : "none",
			value: teams.length,
			unit: "squadre",
			lines: [
				`${players} giocatori unici · ${s.membershipCount} appartenenze`,
				`Partecipazioni delle rose: ${season.V}V · ${season.N}N · ${season.P}P`,
			],
		},
		{
			name: "Gare",
			path: "/ds/gare",
			status: lastWeekRecord[2] > lastWeekRecord[0] ? "warn" : "ok",
			value: nextWeekMatches,
			unit: "gare uniche in 7 giorni",
			lines: [
				lastWeek.length
					? `Partecipazioni ultimi 7 giorni: ${lastWeekRecord[0]}V · ${lastWeekRecord[1]}N · ${lastWeekRecord[2]}P`
					: "Nessun risultato negli ultimi 7 giorni",
				`${s.trainingsWeek} allenamenti conclusi negli ultimi 7 giorni`,
			],
		},
		{
			name: "Team manager",
			path: "/ds/team-manager",
			status:
				attendanceRate === null && !toOrganize && !sheets
					? "none"
					: attendanceSummary.unknown > 0 ||
						  toOrganize ||
						  (attendanceRate !== null && attendanceRate < 0.8)
						? "warn"
						: "ok",
			value: pct(attendanceRate),
			unit: "presenze su partecipazioni attese · 14 giorni",
			coverage: attendanceCoverage(attendanceSummary),
			lines: [
				toOrganize
					? `${toOrganize} partecipazioni delle rose con dettagli da sistemare`
					: "Gare della settimana organizzate",
				`${sheets} distinte gara da completare`,
				attendanceCoverage(attendanceSummary),
			],
		},
		{
			name: "Performance",
			path: "/ds/performance",
			status:
				avgTQR === null
					? "none"
					: lowTQR >= 5
						? "alert"
						: tqrCoverage < 0.6 || lowTQR > 0
							? "warn"
							: "ok",
			value: selectedWellness
				? avgTQR !== null
					? avgTQR.toLocaleString("it-IT", {
							minimumFractionDigits: 1,
							maximumFractionDigits: 1,
						})
					: "—"
				: "Per rosa",
			unit: "TQR · ultimi 7 giorni",
			coverage: selectedWellness
				? "TQR " +
					wellnessCoverage(selectedWellness.tqr, selectedWellness.expected) +
					" · RPE " +
					wellnessCoverage(selectedWellness.rpe, selectedWellness.expected)
				: "Seleziona una rosa; medie e coperture separate in Performance",
			lines: [
				selectedWellness
					? "TQR " +
						wellnessCoverage(selectedWellness.tqr, selectedWellness.expected) +
						" · RPE " +
						wellnessCoverage(selectedWellness.rpe, selectedWellness.expected)
					: "Media sulle persone uniche con dati",
				`${lowTQR} segnalazioni DS sperimentali di recupero basso`,
			],
		},
		{
			name: "Area medica",
			path: "/ds/medica",
			status:
				injuredRatio > 0.1 ? "alert" : injuredRatio > 0.05 ? "warn" : "ok",
			value: s.activeInjuries.length,
			unit: "infortunati",
			lines: [
				`${returning} episodi con dimissione sportiva entro 7 giorni`,
				`${medicalDays.days ?? "—"} giorni-persona persi · ${medicalDays.known}/${medicalDays.total} cartelle · stagione fino a oggi`,
			],
		},
		{
			name: "Mercato & contratti",
			path: "/ds/mercato",
			status: exp90.length ? "alert" : exp180.length || gaps ? "warn" : "ok",
			value: exp180.length,
			unit: "contratti in scadenza (6 mesi)",
			lines: [
				`Monte ingaggi ${formatEuro(s.monthlySalary)}/mese · ${dsSalaryCoverage(s)}`,
				gaps
					? `${gaps} reparti da integrare`
					: "Reparti coperti in tutte le squadre",
			],
		},
		{
			name: "Scouting",
			path: "/ds/scouting",
			status: s.pipeline.length ? "ok" : "none",
			value: pipelineCount("target") + pipelineCount("negotiating"),
			unit: "obiettivi attivi",
			lines: [
				`${s.pipeline.length} giocatori in pipeline (${PIPELINE_STAGES.map((st) => pipelineCount(st.id)).join("/")})`,
				`${observed14} gare da osservare in 14 giorni`,
			],
		},
		{
			name: "Staff",
			path: "/ds/staff",
			status: teamsNoStaff ? "warn" : s.staff.length ? "ok" : "none",
			value: s.staff.length,
			unit: "membri staff",
			lines: [
				teamsNoStaff
					? `${teamsNoStaff} squadre senza staff assegnato`
					: "Tutte le squadre hanno uno staff",
			],
		},
		{
			name: "Segreteria",
			preview: s.feesPreview,
			path: "/ds/segreteria",
			status:
				certIssues || fees.overdue > 0
					? certIssues
						? "alert"
						: "warn"
					: fees.count
						? "ok"
						: "none",
			value: fees.expected ? pct(fees.paid / fees.expected) : "—",
			unit: s.feesPreview ? "quote incassate (demo)" : "quote incassate",
			lines: [
				fees.overdue
					? `${formatEuro(fees.overdue)} di rate scadute`
					: "Nessuna rata scaduta",
				certificates && !certificates.tracked
					? "Certificati medici non ancora inseriti"
					: certIssues === null
						? "Certificati medici: verifica in corso"
						: `${certIssues} certificati medici da regolarizzare`,
			],
		},
		{
			name: "Economia",
			path: "/ds/economia",
			status:
				budgetUse === null
					? "none"
					: budgetUse > 1
						? "alert"
						: budgetUse > 0.9
							? "warn"
							: "ok",
			value: budgetUse !== null ? pct(budgetUse) : formatEuro(annualSalary),
			unit:
				budgetUse !== null
					? "budget ingaggi impegnato"
					: "ingaggi annualizzati (mensile × 12)",
			lines: [
				s.finance.budget
					? `Budget ${formatEuro(s.finance.budget)}`
					: "Budget non disponibile nel contesto DS; consulta Finanza",
				`${s.finance.movements.length} movimenti di mercato`,
			],
		},
		{
			name: "Strutture & logistica",
			path: "/ds/strutture",
			status: "preview",
			value: s.venues.length || "—",
			unit: "campi censiti",
			lines: ["Occupazione campi, manutenzioni, trasporti"],
			preview: true,
		},
		{
			name: "Commerciale",
			path: "/ds/commerciale",
			status: "preview",
			value: "—",
			unit: "sponsor attivi",
			lines: ["Sponsor, comunicazione, iniziative"],
			preview: true,
		},
	];
};

function ChangeRow({icon: Icon, title, sub, onClick}) {
	return (
		<div className="ds-change" onClick={onClick}>
			<span className="ds-change__icon">
				{Icon && <Icon fontSize="small" />}
			</span>
			<div className="ds-change__body">
				<div className="ds-change__title">{title}</div>
				<div className="ds-change__sub">{sub}</div>
			</div>
		</div>
	);
}

function DsHomePage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();

	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({
		handleNotification,
	});
	const {data: certificates} = useClubCertificates({snapshot: s});
	const firstName = useSelector((state) => state?.user?.firstName);
	const [previousVisit] = useState(loadPreviousVisit);
	const [seen, setSeen] = useState(loadSeenActivities);
	const [listMode, setListMode] = useState("mine");

	const areas = s ? buildAreas(s, certificates) : [];
	const health = healthOf(areas);
	const available = s ? s.totalPlayers - s.activeInjuries.length : null;
	const exp180 = s
		? s.contracts.filter((c) => c.daysLeft !== null && c.daysLeft <= 180).length
		: null;

	const activities = buildActivities(s, certificates);
	const openActivities = activities.filter((a) => !seen[a.id]);
	const decisions = openActivities.filter((a) => a.decision);
	const urgent = openActivities.filter((a) => a.severity === "high").length;
	const mode = listMode === "mine" && !decisions.length ? "all" : listMode;
	const todo = mode === "mine" ? decisions : openActivities;
	const changes = buildChanges(s, activities, previousVisit);

	useEffect(() => {
		if (s)
			saveCurrentVisit(
				buildActivities(s.full || s, certificates).map((a) => a.id),
			);
	}, [s?.loadedAt]); // eslint-disable-line react-hooks/exhaustive-deps

	// Il report riguarda sempre il club intero, anche con il filtro squadra
	const exportWeeklyReport = () => {
		try {
			generateDecisionBriefPDF(briefFromSnapshot(s.full || s));
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const toggleSeen = (id) => {
		const next = {...seen};
		if (next[id]) delete next[id];
		else next[id] = new Date().toISOString();
		setSeen(next);
		saveSeenActivities(next);
	};

	const timelineFrom = moment().subtract(1, "days").startOf("day");
	const timelineTo = moment().add(3, "days").endOf("day");
	const timelineDays = groupByDay(
		buildTimeline(s).filter((e) =>
			moment(e.date).isBetween(timelineFrom, timelineTo, undefined, "[]"),
		),
	);

	const heroStats = s
		? [
				{
					label: "Giocatori unici",
					value: s.totalPlayers,
					sub:
						s.teams.length === 1
							? "in 1 squadra"
							: `in ${s.teams.length} squadre`,
				},
				{
					label: "Senza infortunio attivo",
					value: `${available}/${s.totalPlayers} (${s.totalPlayers ? Math.round((100 * available) / s.totalPlayers) : "—"}%)`,
					sub: "giocatori · stato attuale in cartella",
				},
				{
					label: "Infortunati",
					value: s.activeInjuries.length,
					sub: "attualmente",
					tone: s.activeInjuries.length ? "red" : undefined,
				},
				{
					label: "Cose da fare",
					value: openActivities.length,
					sub: `${urgentLabel(urgent)} · ${decisions.length} tue decisioni`,
					tone: urgent ? "red" : undefined,
				},
				{
					label: "Contratti",
					value: exp180,
					sub: "in scadenza entro 6 mesi",
					tone: exp180 ? "yellow" : undefined,
				},
			]
		: [];

	const changeRows = changes
		? [
				...changes.newActivities.map((a) => ({
					id: "a-" + a.id,
					icon: DEPARTMENT_ICONS[a.department],
					title: a.title,
					sub: `Nuova attività · ${departmentLabel(a.department)}`,
					path: a.path,
				})),
				...changes.events
					.slice()
					.reverse()
					.map((e) => ({
						id: "e-" + e.id,
						icon: DEPARTMENT_ICONS[e.department],
						title: e.title,
						sub: `${dayLabel(e.date)} ${moment(e.date).format("HH:mm")} · ${departmentLabel(e.department)}`,
						path: e.path,
					})),
			]
		: [];

	return (
		<Page>
			<div className="ds">
				<div className="ds-hero">
					<div>
						<div className="ds-hero__eyebrow">
							{moment().format("dddd D MMMM")}
							{s?.season?.name ? " · " + s.season.name : ""}
							{s?.teamScope ? " · " + s.teams[0].name : ""}
						</div>
						<h1 className="ds-hero__title">
							{greeting()}
							{firstName ? ", " + firstName : ""}
						</h1>
						<div className="ds-hero__subtitle">
							{s
								? openActivities.length
									? `${decisions.length} ${decisions.length === 1 ? "decisione ti aspetta" : "decisioni ti aspettano"}; nei reparti ${openActivities.length === 1 ? "c'è 1 attività aperta" : `ci sono ${openActivities.length} attività aperte`}, ${urgentLabel(urgent)}.`
									: "Nessuna attività aperta nei reparti: tutto sotto controllo."
								: "Sto raccogliendo i dati di tutte le aree del club…"}
						</div>
						<div className="ds-hero__actions">
							<DsTeamSelect scope={scope} dark />
							<button
								className={"ds-btn ds-btn--ghost" + (loading ? " is-busy" : "")}
								onClick={reload}
								disabled={loading}
							>
								<RefreshIcon fontSize="small" className="ds-btn__spin" />
								{loading
									? "Aggiorno…"
									: s
										? "Aggiornato alle " + moment(s.loadedAt).format("HH:mm")
										: "Aggiorna"}
							</button>
							<button
								className="ds-btn ds-btn--ghost"
								onClick={exportWeeklyReport}
								disabled={!s}
							>
								<PictureAsPdfIcon fontSize="small" /> Report settimanale
							</button>
						</div>
					</div>
					{s && (
						<div className="ds-hero__health">
							<DsRing value={health.value} tone={health.tone} />
							<div>
								<div className="ds-hero__health-label">
									Indice sperimentale DS
								</div>
								<div className="ds-hero__health-text">
									{health.text} · euristica, esempi esclusi
								</div>
							</div>
						</div>
					)}
					{s && (
						<div className="ds-hero__stats">
							{heroStats.map((st) => (
								<div
									key={st.label}
									className={
										"ds-hero__stat" +
										(st.tone ? " ds-hero__stat--" + st.tone : "")
									}
								>
									<div className="ds-hero__stat-label">{st.label}</div>
									<div className="ds-hero__stat-value">{st.value ?? "—"}</div>
									<div className="ds-hero__stat-sub">{st.sub}</div>
								</div>
							))}
						</div>
					)}
				</div>

				<DsSkeleton visible={loading && !s} />

				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsResultWarnings teams={s.teams} />

						{changes && (
							<DsSection
								title="Novità dall'ultima visita"
								hint={`Dal ${moment(changes.since).format("dddd D MMMM [alle] HH:mm")}`}
							>
								{changeRows.length ? (
									<div className="ds-changes">
										{changeRows.slice(0, 6).map((c) => (
											<ChangeRow
												key={c.id}
												{...c}
												onClick={c.path ? () => navigate(c.path) : undefined}
											/>
										))}
										{changeRows.length > 6 && (
											<DsLink to="/ds/timeline">
												Altre {changeRows.length - 6} novità
											</DsLink>
										)}
									</div>
								) : (
									<div className="ds-muted ds-small">
										Nessuna novità: tutto come l'hai lasciato.
									</div>
								)}
							</DsSection>
						)}

						<div className="ds-two-cols">
							<DsSection
								title="Cose da fare"
								action={<DsLink to="/ds/attivita">Tutte</DsLink>}
							>
								<div className="mb-2">
									<DsChips
										value={mode}
										onChange={setListMode}
										options={[
											{
												value: "mine",
												label: "Le mie decisioni",
												count: decisions.length,
											},
											{
												value: "all",
												label: "Tutti i reparti",
												count: openActivities.length,
											},
										]}
									/>
								</div>
								{todo.length ? (
									todo
										.slice(0, 6)
										.map((a) => (
											<DsActivity
												key={a.id}
												item={a}
												seen={!!seen[a.id]}
												onToggleSeen={() => toggleSeen(a.id)}
												onOpen={a.path ? () => navigate(a.path) : undefined}
											/>
										))
								) : (
									<DsEmpty>
										Nessuna attività aperta. Tutto sotto controllo.
									</DsEmpty>
								)}
								{todo.length > 6 && (
									<DsLink to="/ds/attivita">Altre {todo.length - 6}</DsLink>
								)}
							</DsSection>

							<DsSection
								title="Linea temporale"
								hint="Da ieri ai prossimi 3 giorni"
								action={<DsLink to="/ds/timeline">Completa</DsLink>}
							>
								<div className="ds-timeline">
									{timelineDays.map((day) => (
										<div
											key={day.key}
											className={
												"ds-timeline__day" + (day.today ? " is-today" : "")
											}
										>
											<div className="ds-timeline__day-label">
												{dayLabel(day.key)}
											</div>
											{day.items.slice(0, 4).map((e) => (
												<DsTimelineEvent
													key={e.id}
													event={e}
													compact
													onOpen={e.path ? () => navigate(e.path) : undefined}
												/>
											))}
											{day.items.length > 4 && (
												<DsLink to="/ds/timeline">
													Altri {day.items.length - 4} avvenimenti
												</DsLink>
											)}
											{!day.items.length && (
												<div className="ds-muted ds-small">
													Nessun avvenimento.
												</div>
											)}
										</div>
									))}
								</div>
							</DsSection>
						</div>

						<DsSection
							title="Tutte le aree"
							hint="Il semaforo di ogni area del club"
						>
							<div className="ds-strip">
								{areas.map((a) => {
									const Icon = AREA_ICONS[a.name];
									return (
										<div
											key={a.name}
											className={"ds-strip__item ds-strip__item--" + a.status}
											onClick={() => navigate(a.path)}
											title={[STATUS_LABEL[a.status], ...a.lines].join(" · ")}
										>
											<span className="ds-strip__icon">
												{Icon && <Icon fontSize="small" />}
											</span>
											<span className="ds-strip__body">
												<span className="ds-strip__name">{a.name}</span>
												<span className="ds-strip__value">
													{a.value} <small>{a.unit}</small>
													{a.coverage && (
														<small
															style={{display: "block", whiteSpace: "normal"}}
														>
															{a.coverage}
														</small>
													)}
												</span>
											</span>
										</div>
									);
								})}
							</div>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsHomePage;
